import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  GetPromotions,
  GetProducts,
  GetStores,
  GetCategories,
  AddPromotion,
  UpdatePromotion,
  DeletePromotion,
} from "../../requests";
import { initialPromotion, mapLegacyCouponToPromotion, mapPromotionToLegacyPayload } from "../../utils/promotionAdapter";
import { PROMOTION_STEPS } from "../../constants/promotionConstants";
import StepBasicInfo from "./PromotionSteps/StepBasicInfo";
import StepQualification from "./PromotionSteps/StepQualification";
import StepReward from "./PromotionSteps/StepReward";
import StepPolicy from "./PromotionSteps/StepPolicy";
import StepRestrictions from "./PromotionSteps/StepRestrictions";
import StepPreview from "./PromotionSteps/StepPreview";
import styles from "./styles.module.css";

const PromotionBuilder = () => {
  const [promotions, setPromotions] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [promotion, setPromotion] = useState(initialPromotion);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPromotionId, setSelectedPromotionId] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetId, setTargetId] = useState(null);

  const steps = useMemo(() => {
    if (promotion.reward.type !== "freeProduct") {
      return PROMOTION_STEPS.filter((step) => step !== "Reward Policy");
    }
    return PROMOTION_STEPS;
  }, [promotion.reward.type]);

  const stepCount = steps.length;

  useEffect(() => {
    if (activeStep >= steps.length) {
      setActiveStep(steps.length - 1);
    }
  }, [steps, activeStep]);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await GetPromotions(token);
      setPromotions(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Unable to load promotions");
    } finally {
      setLoading(false);
    }
  };

  const fetchMenu = async () => {
    try {
      const response = await GetProducts({});
      setMenuItems(response.data.data || response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await GetCategories();
      // /category returns { data: [ { id, categoryId, category, ... } ] }
      const list = response.data?.data || response.data || [];
      setCategories(list);
    } catch (error) {
      console.error("Failed to load categories", error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await GetStores();
      setStores(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Unable to load stores");
    }
  };

  useEffect(() => {
    fetchPromotions();
    fetchMenu();
    fetchCategories();
    fetchStores();
  }, []);

  const editPromotion = (item) => {
    setSelectedPromotionId(item.id || item._id || item.code);
    setPromotion(mapLegacyCouponToPromotion(item));
    setActiveStep(0);
  };

  const resetBuilder = () => {
    setSelectedPromotionId(null);
    setPromotion(initialPromotion);
    setActiveStep(0);
  };

  const updateField = (field, value) => {
    setPromotion((prev) => ({ ...prev, [field]: value }));
  };

  const updateQualification = (field, value) => {
    setPromotion((prev) => ({
      ...prev,
      qualification: { ...prev.qualification, [field]: value },
    }));
  };

  const updateReward = (field, value) => {
    setPromotion((prev) => ({
      ...prev,
      reward: { ...prev.reward, [field]: value },
    }));
  };

  const updatePolicy = (field, value) => {
    setPromotion((prev) => ({
      ...prev,
      policy: { ...prev.policy, [field]: value },
    }));
  };

  const updateRestrictions = (field, value) => {
    setPromotion((prev) => ({
      ...prev,
      restrictions: { ...prev.restrictions, [field]: value },
    }));
  };

  const handleNext = () => {
    setActiveStep((current) => Math.min(current + 1, stepCount - 1));
  };

  const handleBack = () => {
    setActiveStep((current) => Math.max(current - 1, 0));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const payload = mapPromotionToLegacyPayload(promotion);
      if (selectedPromotionId) {
        await UpdatePromotion(token, selectedPromotionId, payload);
        toast.success("Promotion updated successfully");
      } else {
        await AddPromotion(token, payload);
        toast.success("Promotion created successfully");
      }
      resetBuilder();
      fetchPromotions();
    } catch (error) {
      console.error(error);
      toast.error("Unable to save promotion");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id) => {
    setTargetId(id);
    setConfirmModal(true);
  };

  const confirmDelete = async () => {
    setConfirmModal(false);
    if (!targetId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await DeletePromotion(token, targetId);
      toast.success("Promotion deleted");
      fetchPromotions();
    } catch (error) {
      console.error(error);
      toast.error("Unable to delete promotion");
    } finally {
      setLoading(false);
      setTargetId(null);
    }
  };

  const activeStepContent = () => {
    const currentStepLabel = steps[activeStep];
    switch (currentStepLabel) {
      case "Basic Information":
        return <StepBasicInfo promotion={promotion} onChange={updateField} stores={stores} />;
      case "Qualification Rules":
        return (
          <StepQualification
            promotion={promotion}
            onChange={updateQualification}
            menuItems={menuItems}
            categories={categories}
          />
        );
      case "Reward Rules":
        return <StepReward promotion={promotion} onChange={updateReward} menuItems={menuItems} categories={categories} />;
      case "Reward Policy":
        return <StepPolicy promotion={promotion} onChange={updatePolicy} />;
      case "Restrictions":
        return <StepRestrictions promotion={promotion} onChange={updateRestrictions} />;
      case "Preview":
        return <StepPreview promotion={promotion} menuItems={menuItems} categories={categories} />;
      default:
        return null;
    }
  };

  return (
    <div>
      <Card className="mb-4">
        <CardBody>
          <Row className="align-items-center mb-3">
            <Col>
              <h4 className="card-title">Promotion Management</h4>
              <p className="card-title-desc">
                Create structured, rule-based promotions with full preview and legacy compatibility.
              </p>
            </Col>
            <Col className="text-end">
              <Button color="primary" onClick={resetBuilder}>
                New Promotion
              </Button>
            </Col>
          </Row>

          <div className={styles.stepperContainer}>
            {steps.map((label, index) => (
              <div
                key={label}
                className={`${styles.stepItem} ${activeStep === index ? styles.activeStep : ""}`}
              >
                <span className={styles.stepNumber}>{index + 1}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>

          <Card className="mt-4">
            <CardBody>
              {activeStepContent()}
              <div className="d-flex justify-content-between mt-4">
                <Button color="secondary" outline disabled={activeStep === 0} onClick={handleBack}>
                  Back
                </Button>
                <div>
                  {activeStep < stepCount - 1 && (
                    <Button color="primary" onClick={handleNext} className="me-2">
                      Continue
                    </Button>
                  )}
                  {activeStep === stepCount - 1 && (
                    <Button color="success" onClick={handleSave} disabled={saving}>
                      {saving ? <Spinner size="sm" /> : "Save Promotion"}
                    </Button>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <Row className="align-items-center mb-3">
            <Col>
              <h5 className="mb-0">Existing Promotions</h5>
            </Col>
          </Row>
          {loading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : promotions.length ? (
            <Table responsive striped>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Expiry</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((item, index) => (
                  <tr key={item.id || item._id || `${item.code}-${index}`}>
                    <th scope="row">{index + 1}</th>
                    <td>{item.heading || item.name || "—"}</td>
                    <td>{item.code || "—"}</td>
                    <td>
                      <Badge color={item.status === "enable" || item.status === "active" ? "success" : "secondary"} pill>
                        {item.status === "enable" ? "Enabled" : item.status}
                      </Badge>
                    </td>
                    <td>{item.discountType || item.mode || "—"}</td>
                    <td>{item.expiryDate || "—"}</td>
                    <td>
                      <Button size="sm" color="info" className="me-2" onClick={() => editPromotion(item)}>
                        Edit
                      </Button>
                      <Button size="sm" color="danger" onClick={() => handleDeleteClick(item.id || item._id || item.code)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert color="info">No promotions found yet.</Alert>
          )}
        </CardBody>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered>
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this promotion? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>Delete</Button>{' '}
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PromotionBuilder;
